import { motion, AnimatePresence } from 'motion/react';
import { AlertTriangle } from 'lucide-react';

const CRITERION_STYLES: Record<string, { bg: string; border: string; text: string; accent: string; glow: string }> = {
  LEARN: {
    bg: 'bg-teal-50 dark:bg-teal-950/40',
    border: 'border-teal-200 dark:border-teal-800',
    text: 'text-teal-800 dark:text-teal-200',
    accent: 'bg-teal-500',
    glow: 'shadow-teal-500/30',
  },
  EXIT: {
    bg: 'bg-rose-50 dark:bg-rose-950/40',
    border: 'border-rose-200 dark:border-rose-800',
    text: 'text-rose-800 dark:text-rose-200',
    accent: 'bg-rose-500',
    glow: 'shadow-rose-500/30',
  },
};

const CHIP_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  LEARN: { bg: 'bg-teal-100 dark:bg-teal-900', text: 'text-teal-700 dark:text-teal-300', border: 'border-teal-300 dark:border-teal-700' },
  EXIT:  { bg: 'bg-rose-100 dark:bg-rose-900', text: 'text-rose-700 dark:text-rose-300', border: 'border-rose-300 dark:border-rose-700' },
};

interface PressHarderBannerProps {
  criterion: string;
  message: string;
  isVisible: boolean;
}

export function PressHarderBanner({ criterion, message, isVisible }: PressHarderBannerProps) {
  const style = CRITERION_STYLES[criterion] ?? CRITERION_STYLES.LEARN;
  const chip = CHIP_STYLES[criterion] ?? CHIP_STYLES.LEARN;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -20, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.97 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          className={`relative rounded-xl border ${style.bg} ${style.border} overflow-hidden shadow-lg ${style.glow}`}
        >
          <div className={`absolute left-0 top-0 bottom-0 w-1 ${style.accent}`} />

          <div className="px-4 py-3 pl-5">
            <div className="flex items-start gap-3">
              <div className={`flex-shrink-0 mt-0.5 p-1.5 rounded-lg ${style.bg}`}>
                <AlertTriangle className={`w-4 h-4 ${style.text}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-bold tracking-wider uppercase ${chip.bg} ${chip.text} ${chip.border}`}>
                    {criterion}
                  </span>
                  <span className={`text-sm font-bold ${style.text}`}>
                    Press harder
                  </span>
                </div>
                <p className={`text-sm leading-relaxed ${style.text} opacity-80`}>
                  {message}
                </p>
              </div>
            </div>
          </div>

          <motion.div
            className={`absolute inset-0 rounded-xl border-2 ${style.border} pointer-events-none`}
            initial={{ opacity: 0.8 }}
            animate={{ opacity: [0.8, 0.2, 0.8] }}
            transition={{ duration: 2, repeat: 2, ease: 'easeInOut' }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
